import { useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring } from "framer-motion";
import { Play } from "lucide-react";
import { ScrollReveal } from "./MaskedReveal";
import { ContourField } from "./ContourField";
import { BRANDS } from "../data/siteData";

const PreviewCard = ({ brand }) => (
  <div className="pointer-events-none w-64 overflow-hidden rounded-2xl border border-[#14110D]/20 bg-[#14110D] shadow-2xl sm:w-80">
    <div className="relative aspect-video">
      <video
        key={brand.id}
        src={brand.video}
        poster={brand.poster || undefined}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="h-full w-full object-cover"
      />
      <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-[#F4F1EA]/95 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#14110D]">
        <Play className="h-2.5 w-2.5 fill-current" /> Преглед
      </span>
    </div>
    <div className="flex items-center justify-between px-4 py-3">
      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#F4F1EA]/70">{brand.name}</span>
      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#C8402A]">{brand.projects} проекта</span>
    </div>
  </div>
);
const BrandMark = ({ brand, active }) =>
  brand.logo ? (
    <span
      className={`flex items-center rounded-2xl border px-3.5 py-2.5 shadow-[0_2px_10px_rgba(20,17,13,0.10)] backdrop-blur-sm transition-all duration-300 ${
        active
          ? "scale-110 border-[#C8402A] bg-[#F4F1EA] shadow-lg"
          : "border-[#14110D]/15 bg-[#F4F1EA]/85 group-hover:scale-105 group-hover:border-[#14110D]/35"
      }`}
    >
      <img src={brand.logo} alt={brand.name} className="h-6 w-auto max-w-[68px] object-contain sm:h-9 sm:max-w-[110px] lg:h-11 lg:max-w-[140px]" />
    </span>
  ) : (
    <span
      className={`rounded-full border px-3 py-1.5 font-serif text-sm tracking-tight backdrop-blur-sm transition-all duration-300 ${
        active
          ? "scale-105 border-[#C8402A] bg-[#C8402A] text-[#F4F1EA]"
          : "border-[#14110D]/20 bg-[#F4F1EA]/85 text-[#14110D] group-hover:border-[#14110D]"
      }`}
    >
      {brand.name}
    </span>
  );


const DotGrid = () => (
  <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
    <defs>
      <pattern id="map-dots" width="26" height="26" patternUnits="userSpaceOnUse">
        <circle cx="1.2" cy="1.2" r="1.2" fill="#14110D" opacity="0.09" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#map-dots)" />
  </svg>
);

const MapGlow = ({ brand }) => (
  <motion.div
    key={brand.id}
    className="pointer-events-none absolute h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full"
    style={{
      left: `${brand.x}%`,
      top: `${(brand.y / 70) * 100}%`,
      background: "radial-gradient(circle, rgba(200,64,42,0.22) 0%, rgba(200,64,42,0.07) 45%, transparent 70%)",
    }}
    initial={{ opacity: 0, scale: 0.6 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.75 }}
    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
    aria-hidden="true"
  />
);

export const PartnersMap = () => {
  const [active, setActive] = useState(null);
  const containerRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 250, damping: 28, mass: 0.6 });
  const sy = useSpring(y, { stiffness: 250, damping: 28, mass: 0.6 });

  const track = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    x.set(e.clientX - rect.left + 28);
    y.set(e.clientY - rect.top - 130);
  };

  return (
    <section
      id="partniori"
      ref={containerRef}
      onMouseMove={track}
      onMouseLeave={() => setActive(null)}
      className="relative border-t border-[#14110D]/10 py-24"
      data-testid="partners-section"
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <ScrollReveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#C8402A]">§ 02</p>
              <h2 className="mt-2 font-serif text-4xl uppercase tracking-tight text-[#14110D] sm:text-5xl lg:text-6xl">
                Клиенти и партньори
              </h2>
            </div>
            <div className="flex gap-8">
              <div>
                <p className="font-serif text-4xl text-[#14110D]">9</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C867E]">бранда</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-[#14110D]">47</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C867E]">проекта</p>
              </div>
            </div>
          </div>
          <p className="mt-4 max-w-lg font-serif text-xl italic text-[#524E48]">
            Посочи точка, за да видиш кадър от работата
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.12} className="mt-12">
          <div
            data-testid="partners-map"
            className="relative overflow-hidden rounded-[2rem] border border-[#14110D]/15 bg-[#EFEBE1] shadow-[inset_0_2px_24px_rgba(20,17,13,0.08)]"
          >
            <ContourField seed={29} opacity={0.16} />
            <DotGrid />
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: "radial-gradient(ellipse at center, transparent 55%, rgba(20,17,13,0.10) 100%)" }}
              aria-hidden="true"
            />

            <div className="relative aspect-[4/5] sm:aspect-[16/9] lg:aspect-[16/8]">
              <AnimatePresence>{active && <MapGlow brand={active} />}</AnimatePresence>

              {BRANDS.map((b) => (
                <button
                  key={b.id}
                  data-testid={`map-city-${b.id}`}
                  aria-label={`${b.name} — ${b.city}`}
                  style={{ left: `${b.x}%`, top: `${(b.y / 70) * 100}%` }}
                  onMouseEnter={() => setActive(b)}
                  onFocus={() => setActive(b)}
                  onClick={() => setActive(b)}
                  className="group absolute -translate-x-1/2 -translate-y-1/2 p-3"
                >
                  <span className="relative block">
                    {active?.id === b.id && (
                      <span className="absolute inset-0 animate-ping rounded-2xl bg-[#C8402A]/30" />
                    )}
                    <BrandMark brand={b} active={active?.id === b.id} />
                  </span>
                </button>
              ))}

              <p className="absolute bottom-3 left-4 font-mono text-[9px] uppercase tracking-[0.22em] text-[#8C867E] sm:bottom-4">
                Фиг. 02 — Пазари на снимане · Посочи точка, за да видиш работата
              </p>
            </div>
          </div>
        </ScrollReveal>

        <div className="mt-6 lg:hidden">
          <AnimatePresence mode="wait">
            {active && (
              <motion.div
                key={`mobile-${active.id}`}
                data-testid="brand-mobile-preview"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="mx-auto w-fit">
                  <PreviewCard brand={active} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <motion.div
        data-testid="brand-video-preview"
        className="pointer-events-none absolute left-0 top-0 z-20 hidden lg:block"
        style={{ x: sx, y: sy }}
      >
        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active.id}
              initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.94, rotate: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <PreviewCard brand={active} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};
